import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../helpers/axios';

import {
  Box, Card, CardContent, Typography, LinearProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Skeleton, Alert, Button,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const BUCKET_COLOR = {
  S1: { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  S2: { bg: '#fff8e1', text: '#f57f17', border: '#ffe082' },
  S3: { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' },
  M1: { bg: '#fce4ec', text: '#c62828', border: '#f48fb1' },
  M2: { bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' },
  X1: { bg: '#e8eaf6', text: '#283593', border: '#9fa8da' },
  X2: { bg: '#e0e0e0', text: '#424242', border: '#bdbdbd' },
  WO: { bg: '#212121', text: '#ffffff', border: '#424242' },
};

const BUCKET_ORDER = ['S1', 'S2', 'S3', 'M1', 'M2', 'X1', 'X2', 'WO'];

const BUCKET_RULES = {
  S1: { target: '90%', amount: '₹1,500', type: 'Flat', note: 'Hit 90% collection to earn ₹1,500' },
  S2: { target: '75%', amount: '₹2,000', type: 'Flat', note: 'Hit 75% collection to earn ₹2,000' },
  S3: { target: '60%', amount: '₹2,000', type: 'Flat', note: 'Hit 60% collection to earn ₹2,000' },
  M1: { target: '50%', amount: '₹2,000', type: 'Flat', note: 'Hit 50% collection to earn ₹2,000' },
  M2: { target: 'Per File', amount: '₹500–₹1,000', type: 'Per File', note: '₹500 if POS < ₹25k; ₹1,000 if POS ≥ ₹25k' },
  X1: { target: 'Per File', amount: '5% collected', type: 'Per File', note: '5% of each payment collected' },
  X2: { target: 'Per File', amount: '10% collected', type: 'Per File', note: '10% of each payment collected' },
  WO: { target: 'Per File', amount: '10% collected', type: 'Per File', note: '10% of each payment collected' },
};

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

function StatCard({ icon, label, value, sub, accentColor = '#667eea', valueColor = '#344054' }) {
  return (
    <Card elevation={0} sx={{
      flex: 1, minWidth: 130,
      border: '1px solid #f0f0f0',
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: '10px',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
    }}>
      <CardContent sx={{ p: '14px 16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ color: accentColor, display: 'flex', fontSize: 16 }}>{icon}</Box>
          <Typography sx={{ fontSize: '10px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            {label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: valueColor, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: '11px', color: '#9e9e9e', mt: '2px' }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function buildBucketProgressMap(dashboardRaw) {
  const map = {};
  (dashboardRaw?.bucketProgress || []).forEach((bp) => { map[bp.bucket] = bp; });
  return map;
}

export default function AgentIncentive({ agentId, initialData }) {
  const [data, setData]                     = useState(!agentId && initialData?.incentivesRaw ? initialData.incentivesRaw : null);
  const [bucketProgressMap, setBucketProgressMap] = useState(!agentId && initialData?.dashboardRaw ? buildBucketProgressMap(initialData.dashboardRaw) : {});
  const [loading, setLoading]               = useState(agentId ? true : !initialData);
  const [error, setError]                   = useState(null);

  useEffect(() => {
    if (!agentId && initialData) return;

    const incentiveUrl = agentId
      ? `/collection-incentive/manager/agents/${agentId}/incentives`
      : '/collection-incentive/agent/incentives';
    const dashUrl = agentId
      ? `/collection-incentive/manager/agents/${agentId}/dashboard`
      : '/collection-incentive/agent/dashboard';

    Promise.all([
      axiosInstance.get(incentiveUrl),
      axiosInstance.get(dashUrl),
    ])
      .then(([incRes, dashRes]) => {
        setData(incRes.data.data);
        setBucketProgressMap(buildBucketProgressMap(dashRes.data.data));
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load incentives'))
      .finally(() => setLoading(false));
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (agentId || !initialData) return;
    setData(initialData.incentivesRaw);
    setBucketProgressMap(buildBucketProgressMap(initialData.dashboardRaw));
    setLoading(false);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = () => {
    const payments = data?.payments || [];
    if (!payments.length) return;
    const cols = ['Loan ID', 'Bucket', 'Payment Amount (INR)', 'Incentive (INR)', 'Type', 'Payment Date', 'Transaction ID'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      cols.join(','),
      ...payments.map((p) => [
        esc(p.loanId), esc(p.bucket), esc(p.paymentAmount),
        esc(p.incentiveAmount), esc(p.incentiveType),
        esc(fmtDate(p.paymentDate)), esc(p.transactionId),
      ].join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incentive-collections-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={90} sx={{ flex: 1, borderRadius: 2 }} />)}
        </Box>
        <Skeleton variant="rounded" height={280} sx={{ mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const d = data || {};
  const earned = d.earned || 0;
  const maxPossible = d.maxPossible || 0;
  const remaining = Math.max(0, maxPossible - earned);
  const earningPct = maxPossible > 0 ? Math.round((earned / maxPossible) * 100) : 0;

  return (
    <Box sx={{ p: '10px' }}>

      {/* ── 4 Summary Stat Cards ── */}
      <Box sx={{ display: 'flex', gap: '12px', mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          icon={<EmojiEventsIcon fontSize="inherit" />}
          label="Earned This Month"
          value={fmt(earned)}
          sub={maxPossible > 0 ? `${earningPct}% of max` : undefined}
          accentColor="#2e7d32"
          valueColor="#2e7d32"
        />
        <StatCard
          icon={<AccountBalanceWalletIcon fontSize="inherit" />}
          label="Max Possible"
          value={fmt(maxPossible)}
          accentColor="#667eea"
        />
        <StatCard
          icon={<HourglassTopIcon fontSize="inherit" />}
          label="Remaining"
          value={fmt(remaining)}
          sub={remaining === 0 && maxPossible > 0 ? 'All targets hit! 🎉' : undefined}
          accentColor={remaining === 0 && maxPossible > 0 ? '#2e7d32' : '#f57f17'}
          valueColor={remaining === 0 && maxPossible > 0 ? '#2e7d32' : '#f57f17'}
        />
        <StatCard
          icon={<ReceiptLongIcon fontSize="inherit" />}
          label="Collections"
          value={fmt(d.totalCollected)}
          sub={d.paymentCount ? `${d.paymentCount} payment${d.paymentCount !== 1 ? 's' : ''}` : undefined}
          accentColor="#9c27b0"
        />
      </Box>

      {/* ── Bucket Incentive Breakdown ── */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', fontWeight: 600, mb: 1.5 }}>
          🏆 Bucket Incentive Breakdown
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                {['Bucket', 'Loans', 'Target', 'Incentive Rule', 'Progress', 'Earned', 'Status'].map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 600, color: '#667085', fontSize: '12px', whiteSpace: 'nowrap', py: '10px' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {BUCKET_ORDER.map((bucketKey) => {
                const row = (d.buckets || []).find((b) => b.bucket === bucketKey) || {};
                const bc = BUCKET_COLOR[bucketKey] || { bg: '#f0f0f0', text: '#344054', border: '#e0e0e0' };
                const rule = BUCKET_RULES[bucketKey] || {};
                const hasLoans = (row.loanCount || 0) > 0;
                const bp = bucketProgressMap[bucketKey] || {};
                const collectionPct = bp.collectionPct || 0;
                // Per-file buckets: "Hit" only when 100% of overdue is collected
                // Flat-target buckets: "Hit" = backend confirms target % is reached
                const isHit = row.isPerFile ? collectionPct >= 100 : row.isHit;
                const showBar = hasLoans && collectionPct > 0;

                return (
                  <TableRow key={bucketKey} sx={{
                    '&:hover': { backgroundColor: '#fafafa' },
                    opacity: hasLoans ? 1 : 0.45,
                  }}>
                    <TableCell>
                      <Chip label={bucketKey} size="small"
                        sx={{ backgroundColor: bc.bg, color: bc.text, fontWeight: 700, fontSize: '11px', border: `1px solid ${bc.border}` }} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#344054' }}>{row.loanCount || 0}</TableCell>
                    <TableCell sx={{ color: '#667085', fontSize: '12px' }}>{rule.target || '—'}</TableCell>
                    <TableCell sx={{ color: '#667085', fontSize: '12px', whiteSpace: 'nowrap' }}>{rule.type || '—'}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {showBar ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(collectionPct, 100)}
                            sx={{
                              flex: 1, height: 6, borderRadius: 3,
                              backgroundColor: '#f0f0f0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: isHit ? '#2e7d32' : '#f57f17',
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '11px', color: '#bdbdbd' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: isHit ? '#2e7d32' : '#bdbdbd' }}>
                      {row.incentive > 0 ? fmt(row.incentive) : '—'}
                    </TableCell>
                    <TableCell>
                      {!hasLoans ? (
                        <Chip label="No Loans" size="small" sx={{ fontSize: '10px', backgroundColor: '#f5f5f5', color: '#9e9e9e' }} />
                      ) : isHit ? (
                        <Chip label="✓ Hit" size="small" sx={{ fontSize: '10px', backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                      ) : (
                        <Chip label="In Progress" size="small" sx={{ fontSize: '10px', backgroundColor: '#fff8e1', color: '#f57f17' }} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── My Collections Verification Table ── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', fontWeight: 600 }}>
            💳 My Collections (Verification)
          </Typography>
          {(d.payments || []).length > 0 && (
            <Button
              size="small"
              onClick={handleExport}
              startIcon={<FileDownloadIcon fontSize="small" />}
              variant="outlined"
              sx={{
                textTransform: 'none', fontSize: '12px', borderRadius: '8px',
                borderColor: '#344054', color: '#344054',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              Export CSV
            </Button>
          )}
        </Box>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                {['Loan ID', 'Bucket', 'Payment ₹', 'Incentive ₹', 'Type', 'Date', 'Txn ID'].map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 600, color: '#667085', fontSize: '12px', whiteSpace: 'nowrap', py: '10px' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(d.payments || []).length > 0 ? (d.payments).map((p, i) => {
                const bc = BUCKET_COLOR[p.bucket] || { bg: '#f0f0f0', text: '#344054', border: '#e0e0e0' };
                return (
                  <TableRow key={p.transactionId || i} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 700, color: '#344054', fontSize: '12px' }}>{p.loanId || '—'}</TableCell>
                    <TableCell>
                      {p.bucket && (
                        <Chip label={p.bucket} size="small"
                          sx={{ backgroundColor: bc.bg, color: bc.text, fontWeight: 700, fontSize: '11px', border: `1px solid ${bc.border}` }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#2e7d32', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {fmt(p.paymentAmount)}
                    </TableCell>
                    <TableCell sx={{ color: p.incentiveAmount > 0 ? '#2e7d32' : '#bdbdbd', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {p.incentiveAmount > 0 ? fmt(p.incentiveAmount) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.incentiveType === 'TARGET_BASED' ? 'Target Hit' : 'Per File'}
                        size="small"
                        sx={{
                          fontSize: '10px',
                          backgroundColor: p.incentiveType === 'TARGET_BASED' ? '#e8f5e9' : '#fff3e0',
                          color: p.incentiveType === 'TARGET_BASED' ? '#2e7d32' : '#e65100',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', color: '#667085', whiteSpace: 'nowrap' }}>
                      {fmtDate(p.paymentDate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '11px', color: '#9e9e9e', maxWidth: 120 }}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.transactionId || '—'}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                    No collection records this month.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Rules Section ── */}
      <Box>
        <Typography sx={{ fontFamily: 'Inter-Medium', fontSize: '15px', color: '#344054', fontWeight: 600, mb: 1.5 }}>
          📋 Incentive Rules
        </Typography>
        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, backgroundColor: '#f9fafb', borderBottom: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: '12px', color: '#667085' }}>
              Incentives are calculated monthly. Flat amounts are credited when the collection % target for a bucket is met.
              Per-file incentives are credited per individual payment received.
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#ffffff' }}>
                {['Bucket', 'Target', 'Amount / Rate', 'Type', 'Notes'].map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 600, color: '#667085', fontSize: '12px', whiteSpace: 'nowrap', py: '10px' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {BUCKET_ORDER.map((b) => {
                const bc = BUCKET_COLOR[b] || { bg: '#f0f0f0', text: '#344054', border: '#e0e0e0' };
                const rule = BUCKET_RULES[b];
                return (
                  <TableRow key={b} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell>
                      <Chip label={b} size="small"
                        sx={{ backgroundColor: bc.bg, color: bc.text, fontWeight: 700, fontSize: '11px', border: `1px solid ${bc.border}` }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', color: '#344054' }}>{rule.target}</TableCell>
                    <TableCell sx={{ fontSize: '12px', color: '#2e7d32', fontWeight: 700 }}>{rule.amount}</TableCell>
                    <TableCell>
                      <Chip label={rule.type} size="small" sx={{ fontSize: '10px', backgroundColor: '#f5f5f5', color: '#667085' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '11px', color: '#9e9e9e' }}>{rule.note}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>

    </Box>
  );
}
